// ============================================================================
// NOTICE TYPE HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { noticeTypeService } from '../../api/services/noticeTypeService';
import type {
  NoticeType,
  NoticeTypeListItem,
  CreateNoticeTypeRequest,
  UpdateNoticeTypeRequest,
  NoticeTypeSearchRequest,
} from '../../api/types/noticeType.types';
import type { ID } from '../../api/types';

export const useNoticeTypes = (params?: NoticeTypeSearchRequest) => {
  const [data, setData] = useState<NoticeTypeListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchNoticeTypes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await noticeTypeService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchNoticeTypes(); }, [fetchNoticeTypes]);

  return { data, loading, error, pagination, refetch: fetchNoticeTypes };
};

export const useNoticeType = (id: ID | undefined) => {
  const [data, setData] = useState<NoticeType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchNoticeType = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await noticeTypeService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchNoticeType(); }, [fetchNoticeType]);

  return { data, loading, error, refetch: fetchNoticeType };
};

export const useCreateNoticeType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createNoticeType = async (
    data: CreateNoticeTypeRequest,
    options?: { onSuccess?: (nt: NoticeType) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await noticeTypeService.create(data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { createNoticeType, loading, error };
};

export const useUpdateNoticeType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateNoticeType = async (
    id: ID, data: UpdateNoticeTypeRequest,
    options?: { onSuccess?: (nt: NoticeType) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await noticeTypeService.update(id, data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { updateNoticeType, loading, error };
};

export const useDeleteNoticeType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteNoticeType = async (
    id: ID,
    options?: { onSuccess?: () => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      await noticeTypeService.delete(id);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { deleteNoticeType, loading, error };
};
